import { faArrowDown, faArrowUp } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useReducer, useRef } from "react";
import { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ColumnDescription, SelectRowProps } from "react-bootstrap-table-next";
import Table from "..";
import { useTransformToRef } from "../../../hooks/useTransformToRef";
import {
    ListarGrupoNaoVinculadosPorEtapaFixaController,
    ListarGrupoVinculadosPorEtapaFixaController,
} from "../../../Controllers/Geral/Etapas/EtapasControllers";
import {
    IListarEtapaGrupoNaoVinculadosFixo,
    IListarEtapaGrupoVinculadosFixo,
} from "../../../Interfaces/Geral/Etapas/EtapasInterfaces";
import { ACESSOS } from "../../../Routes/Acessos";

interface ITabela<T> {
    text: string;
    key: keyof T;
    dataField: keyof T;
    overlay?: keyof T;
}
interface VinculadorSimplesProps<T> {
    id: number;
    title?: string;
    idModulo?: number;
    tabelaVinculados: ITabela<T>;
    tabelaDesvinculados: ITabela<T>;
    flEditarCategoriaAtendimento?: boolean;

    listarVinculados: (id: number) => Promise<Array<T>>;
    listarNaoVinculados: (id: number) => Promise<Array<T>>;

    // jeito antigo
    acoes?: {
        vincular: (id: number, row: T) => Promise<void>;
        desvincular: (row: T) => Promise<void>;
    };
    multiplo?: {
        vincular: (id: number, rows: T[]) => Promise<any>;
        desvincular: (rows: T[]) => Promise<any>;
    };
}
function FindAndRemove<T>(array: Array<T>, find: (item: T) => boolean) {
    const cloneArray = Array.from(array);
    const index = cloneArray.findIndex(find);
    array.splice(index, 1);
}

function getArrayReducer<T>(equals: (itemA: T, itemB: T) => boolean) {
    return function arrayReducer(
        state: Array<T>,
        options: { action: "remove" | "add"; newItem: T } | undefined | T[]
    ) {
        const clone = options ? state.map((i) => i) : [];
        if (options) {
            if (Array.isArray(options)) {
                return options;
            } else {
                switch (options.action) {
                    case "add":
                        clone.push(options.newItem);
                        break;
                    case "remove":
                        FindAndRemove(clone, (item) =>
                            equals(item, options.newItem)
                        );
                        break;
                    default:
                        break;
                }
            }
        }
        return clone;
    };
}

export default function VinculadorSimples<T extends object>(
    props: VinculadorSimplesProps<T>
) {
    const arrayReducer = useRef(
        getArrayReducer<T>(
            (itemA, itemB) =>
                itemA[props.tabelaVinculados.key] ===
                itemB[props.tabelaVinculados.key]
        )
    );

    const [vinculados, setVinculados] = useState([] as Array<T>);
    const [Desvinculados, setDesvinculados] = useState([] as Array<T>);

    const [vinculadosFixos, setVinculadosFixos] =
        useState<IListarEtapaGrupoVinculadosFixo[]>();
    const [DesvinculadosFixos, setDesvinculadosFixos] =
        useState<IListarEtapaGrupoNaoVinculadosFixo[]>();
    const [loading, setLoading] = useState(false);

    const [lstVinculadosChecked, setLstVinculadosChecked] = useReducer(
        arrayReducer.current,
        []
    );
    const [lstNaoVinculadosChecked, setLstNaoVinculadosChecked] = useReducer(
        arrayReducer.current,
        []
    );
    const { id, listarVinculados, listarNaoVinculados } = props;

    const listarVinculadosRef = useTransformToRef(listarVinculados);
    const listarNaoVinculadosRef = useTransformToRef(listarNaoVinculados);

    const listarVinculadosFixoRef = useTransformToRef(
        ListarGrupoVinculadosPorEtapaFixaController
    );
    const listarNaoVinculadosFixoRef = useTransformToRef(
        ListarGrupoNaoVinculadosPorEtapaFixaController
    );

    const update = useCallback(() => {
        if (id) {
            if (props.flEditarCategoriaAtendimento) {
                if (
                    props.idModulo === ACESSOS.ATENDIMENTO.ID_MODULO ||
                    props.idModulo === ACESSOS.REPASSE.ID_MODULO
                ) {
                    setLoading(true);

                    Promise.all([
                        listarVinculadosRef.current(id),
                        listarNaoVinculadosRef.current(id),
                    ])
                        .then((res) => {
                            const [vinculados, desvinculados] = res;
                            setVinculados(vinculados);
                            setDesvinculados(desvinculados);
                        })
                        .finally(() => setLoading(false));
                } else {
                    setLoading(true);

                    Promise.all([
                        listarVinculadosFixoRef.current(id),
                        listarNaoVinculadosFixoRef.current(id),
                    ])
                        .then((res) => {
                            const [vinculadosFixos, desvinculadosFixos] = res;
                            setVinculadosFixos(vinculadosFixos);
                            setDesvinculadosFixos(desvinculadosFixos);
                        })
                        .finally(() => setLoading(false));
                }
            } else {
                setLoading(true);
                Promise.all([
                    listarVinculadosRef.current(id),
                    listarNaoVinculadosRef.current(id),
                ])
                    .then((res) => {
                        const [vinculados, desvinculados] = res;
                        setVinculados(vinculados);
                        setDesvinculados(desvinculados);
                    })
                    .finally(() => setLoading(false));
            }
        }
    }, [
        id,
        props.idModulo,
        props.flEditarCategoriaAtendimento,
        listarVinculadosRef,
        listarNaoVinculadosRef,
        listarVinculadosFixoRef,
        listarNaoVinculadosFixoRef,
    ]);

    useEffect(() => {
        update();
    }, [update]);

    //Tabela de Vinculadas
    const columnsVinculadas: Array<ColumnDescription<T>> = [
        {
            dataField: props.tabelaVinculados.dataField,
            text: props.tabelaVinculados.text,
            formatter: RowVinculada,
            classes: "border-bottom",
            sort: true,
        },
    ];
    const selectPropsVinculadas: SelectRowProps<T> = {
        mode: "checkbox",
        onSelect: (row, isSelected) => {
            if (isSelected)
                setLstVinculadosChecked({ action: "add", newItem: row });
            else setLstVinculadosChecked({ action: "remove", newItem: row });
        },
        onSelectAll: (isSelected, rows) => {
            if (isSelected) setLstVinculadosChecked(rows);
            else setLstVinculadosChecked(undefined);
        },
    };
    function RowVinculada(cell: string, row: T) {
        return (
            <div className="rowTelas d-flex justify-content-between align-items-center p-2">
                <OverlayTrigger
                    show={props.tabelaVinculados.overlay ? undefined : false}
                    overlay={
                        <Tooltip
                            id={`overlay-${row[props.tabelaVinculados.key]}`}
                        >
                            {props.tabelaVinculados.overlay
                                ? row[props.tabelaVinculados.overlay]
                                : ""}
                        </Tooltip>
                    }
                >
                    <label className="text-break m-0">{cell}</label>
                </OverlayTrigger>
                <button
                    className="btn btn-sm btn-outline-danger font-weight-bold ml-2 d-flex align-items-center justify-content-center flex-nowrap"
                    disabled={loading}
                    onClick={() => {
                        setLoading(true);

                        if (props.multiplo) {
                            props.multiplo
                                .desvincular([row])
                                .then(() =>
                                    setLstVinculadosChecked({
                                        action: "remove",
                                        newItem: row,
                                    })
                                )
                                .finally(update);
                        } else {
                            props.acoes?.desvincular(row).finally(update);
                        }
                    }}
                >
                    <FontAwesomeIcon
                        icon={faArrowDown}
                        size="lg"
                        className="mr-2"
                    />{" "}
                    Desvincular
                </button>
            </div>
        );
    }
    //Tabela Desvinculadas
    const columnsDesvinculadas: Array<ColumnDescription> = [
        {
            dataField: props.tabelaDesvinculados.dataField,
            text: props.tabelaDesvinculados.text,
            formatter: RowDesvinculada,
            classes: "border-bottom",
            sort: true,
        },
    ];
    const selectPropsDesvinculadas: SelectRowProps<T> = {
        mode: "checkbox",
        onSelect: (row, isSelected) => {
            if (isSelected)
                setLstNaoVinculadosChecked({ action: "add", newItem: row });
            else setLstNaoVinculadosChecked({ action: "remove", newItem: row });
        },
        onSelectAll: (isSelected, rows) => {
            if (isSelected) setLstNaoVinculadosChecked(rows);
            else setLstNaoVinculadosChecked(undefined);
        },
    };

    function RowDesvinculada(cell: string, row: T) {
        return (
            <div className="rowTelas d-flex justify-content-between align-items-center p-2">
                <OverlayTrigger
                    show={props.tabelaDesvinculados.overlay ? undefined : false}
                    overlay={
                        <Tooltip
                            id={`overlay-${row[props.tabelaDesvinculados.key]}`}
                        >
                            {props.tabelaDesvinculados.overlay
                                ? row[props.tabelaDesvinculados.overlay]
                                : ""}
                        </Tooltip>
                    }
                >
                    <label className="text-break m-0">{cell}</label>
                </OverlayTrigger>
                <button
                    className="btn btn-sm btn-outline-success font-weight-bold ml-2 d-flex align-items-center justify-content-center flex-nowrap"
                    disabled={loading}
                    onClick={() => {
                        setLoading(true);

                        if (props.multiplo) {
                            props.multiplo
                                .vincular(props.id, [row])
                                .then(() => {
                                    setLstNaoVinculadosChecked(undefined);
                                })
                                .finally(update);
                        } else {
                            props.acoes
                                ?.vincular(props.id, row)
                                .then(() => {
                                    if (props.multiplo)
                                        setLstNaoVinculadosChecked({
                                            action: "remove",
                                            newItem: row,
                                        });
                                })
                                .finally(update);
                        }
                    }}
                >
                    <FontAwesomeIcon
                        icon={faArrowUp}
                        size="lg"
                        className="mr-2"
                    />{" "}
                    Vincular
                </button>
            </div>
        );
    }
    return (
        <>
            <h3>{props.title}</h3>
            <div className="Row2 flex-lg-row-fluid d-flex flex-column flex-lg-row mt-5">
                <div className="col-12 col-lg-6 d-flex flex-column justify-content-between">
                    <Table
                        keyField={props.tabelaVinculados.key.toString()}
                        columns={columnsVinculadas}
                        data={
                            props.flEditarCategoriaAtendimento
                                ? (props.idModulo ===
                                      ACESSOS.ATENDIMENTO.ID_MODULO ||
                                  props.idModulo === ACESSOS.REPASSE.ID_MODULO
                                      ? vinculados
                                      : vinculadosFixos) ?? []
                                : vinculados
                        }
                        loading={loading}
                        classes="tableClass"
                        rowClasses="checkbox-table"
                        bodyClasses="bodyClass"
                        selectRow={
                            props.multiplo ? selectPropsVinculadas : undefined
                        }
                        bordered
                    />
                    {props.multiplo && (
                        <button
                            disabled={lstVinculadosChecked.length === 0}
                            className="btn btn-outline-danger w-100"
                            onClick={() => {
                                props.multiplo
                                    ?.desvincular(lstVinculadosChecked)
                                    .then(() => {
                                        setLstVinculadosChecked(undefined);
                                    })
                                    .finally(update);
                            }}
                        >
                            Desvincular Selecionados
                        </button>
                    )}
                </div>
                <div className="col-12 col-lg-6 d-flex flex-column justify-content-between">
                    <Table
                        keyField={props.tabelaDesvinculados.key.toString()}
                        columns={columnsDesvinculadas}
                        data={
                            props.flEditarCategoriaAtendimento
                                ? (props.idModulo ===
                                      ACESSOS.ATENDIMENTO.ID_MODULO ||
                                  props.idModulo === ACESSOS.REPASSE.ID_MODULO
                                      ? Desvinculados
                                      : DesvinculadosFixos) ?? []
                                : Desvinculados
                        }
                        rowClasses="checkbox-table"
                        loading={loading}
                        classes="tableClass"
                        selectRow={
                            props.multiplo
                                ? selectPropsDesvinculadas
                                : undefined
                        }
                        bodyClasses="bodyClass"
                        bordered
                    />
                    {props.multiplo && (
                        <button
                            disabled={lstNaoVinculadosChecked.length === 0}
                            className="btn btn-outline-success w-100"
                            onClick={() => {
                                props.multiplo
                                    ?.vincular(
                                        props.id,
                                        lstNaoVinculadosChecked
                                    )
                                    .then(() => {
                                        setLstNaoVinculadosChecked(undefined);
                                    })
                                    .finally(update);
                            }}
                        >
                            Vincular Selecionados
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}
